import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { RegCardOptionsHost } from "@/components/documents/RegCardOptions";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Root redirect — the Landing page has been retired.
 *   • Not logged in            → /login
 *   • Super admin              → /platform
 *   • Any other role           → /dashboard
 */
function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined) return null; // still bootstrapping
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/platform" replace />;
  return <Navigate to="/dashboard" replace />;
}
import Dashboard from "@/pages/Dashboard";
import Reports from "@/pages/Reports";
import RosterPage from "@/pages/workforce/RosterPage";
import AttendancePage from "@/pages/workforce/AttendancePage";
import ExecutiveDashboard from "@/pages/dashboard/Executive";
import TeamMembers from "@/pages/workforce/TeamMembers";
import SecurityDashboard from "@/pages/security/SecurityDashboard";
import ForcePasswordChange from "@/pages/security/ForcePasswordChange";
import ReadinessCatalogs from "@/pages/admin/ReadinessCatalogs";
import Housekeeping from "@/pages/housekeeping/Housekeeping";
import MyTasks from "@/pages/housekeeping/MyTasks";
import Engineering from "@/pages/engineering/Engineering";
import MyWorkOrders from "@/pages/engineering/MyWorkOrders";
import Hotels from "@/pages/Hotels";
import Audit from "@/pages/Audit";
import Events from "@/pages/Events";
import Rules from "@/pages/Rules";
import Scheduler from "@/pages/Scheduler";
import Documents from "@/pages/Documents";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import MasterPage from "@/pages/MasterPage";
import Guests from "@/pages/pms/Guests";
import GuestDetail from "@/pages/pms/GuestDetail";
import Stays from "@/pages/pms/Stays";
import StayDetail from "@/pages/pms/StayDetail";
import StayBillPage from "@/pages/pms/StayBillPage";
import Reservations from "@/pages/pms/Reservations";
import ReservationDetail from "@/pages/pms/ReservationDetail";
import ArrivalBoard from "@/pages/pms/ArrivalBoard";
import Quotations from "@/pages/quotations/Quotations";
import QuotationEditor from "@/pages/quotations/QuotationEditor";
import DepartureBoard from "@/pages/pms/DepartureBoard";
import Inventory from "@/pages/inventory/Inventory";
import Folios from "@/pages/finance/Folios";
import FolioWorkspace from "@/pages/finance/FolioWorkspace";
import Payments from "@/pages/finance/Payments";
import CheckoutQueue from "@/pages/checkout/CheckoutQueue";
import CheckoutWorkspace from "@/pages/checkout/CheckoutWorkspace";
import OperationalDemo from "@/pages/design-system/OperationalDemo";
import HkOsGallery from "@/pages/design-system/HkOsGallery";
import HkTrainingApp from "@/pages/hk/training/HkTrainingApp";
import HkTrainingSupervisor from "@/pages/hk/training/HkTrainingSupervisor";
import FrontDeskCommandCentre from "@/pages/fo/FrontDeskCommandCentre";
import ShiftPage from "@/pages/fo/ShiftPage";
import SetupCenter from "@/pages/setup/SetupCenter";
import PlatformShell from "@/pages/platform/PlatformShell";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformHotels from "@/pages/platform/PlatformHotels";
import CreateHotelStep1 from "@/pages/platform/CreateHotelStep1";
import CreateHotelStep2 from "@/pages/platform/CreateHotelStep2";
import PlatformHotelDetail from "@/pages/platform/PlatformHotelDetail";
import PlatformSuperAdmins from "@/pages/platform/PlatformSuperAdmins";
import PlatformAccount from "@/pages/platform/PlatformAccount";
import CreateHotelStep3 from "@/pages/platform/CreateHotelStep3";
import EventMonitor from "@/pages/platform/EventMonitor";
import PlatformSystemHealth from "@/pages/platform/PlatformSystemHealth";
import HotelServicesEditor from "@/pages/hotels/HotelServicesEditor";
import PlatformHotelServices from "@/pages/platform/PlatformHotelServices";
import OperationsPage from "@/pages/operations/OperationsPage";
import HKMissionControl from "@/pages/housekeeping/mission/HKMissionControl";
import HKCopilot from "@/pages/housekeeping/copilot/HKCopilot";
import HKInventoryShell from "@/pages/housekeeping/inventory/HKInventoryShell";
import RoomConsumption from "@/pages/housekeeping/consumption/RoomConsumption";
import HKInventory from "@/pages/housekeeping/inventory/HKInventory";
import HKStorageRequests from "@/pages/housekeeping/inventory/HKStorageRequests";
import HKFloorPars from "@/pages/housekeeping/inventory/HKFloorPars";
import HKUsageLog from "@/pages/housekeeping/inventory/HKUsageLog";
import WorkforceShell from "@/pages/wf/WorkforceShell";
import WorkforceDashboard from "@/pages/wf/WorkforceDashboard";
import WFEmployees from "@/pages/wf/Employees";
import WFSystemAccess from "@/pages/wf/SystemAccess";
import WFPermissionMatrix from "@/pages/wf/PermissionMatrix";
import WFOrganization from "@/pages/wf/Organization";
import WFPolicies from "@/pages/wf/Policies";
import WFDevices from "@/pages/wf/Devices";
import WFGroups from "@/pages/wf/Groups";
import WFApprovals from "@/pages/wf/Approvals";
import WFAudit from "@/pages/wf/Audit";
import SupplyShell from "@/pages/supply/SupplyShell";
import SupplyDashboard from "@/pages/supply/SupplyDashboard";
import SupplyItems from "@/pages/supply/SupplyItems";
import SupplyVendors from "@/pages/supply/SupplyVendors";
import SupplyStores from "@/pages/supply/SupplyStores";
import SupplyStock from "@/pages/supply/SupplyStock";
import SupplyPRs from "@/pages/supply/SupplyPRs";
import SupplyPOs from "@/pages/supply/SupplyPOs";
import SupplyGRNs from "@/pages/supply/SupplyGRNs";
import SupplySettings from "@/pages/supply/SupplySettings";
import DigitalTwin from "@/pages/supply/twin/DigitalTwin";
import MaintenanceShell from "@/pages/maintenance/MaintenanceShell";
import MaintenanceDashboard from "@/pages/maintenance/MaintenanceDashboard";
import MaintenanceAssets from "@/pages/maintenance/MaintenanceAssets";
import MaintenanceWorkOrders from "@/pages/maintenance/MaintenanceWorkOrders";
import MaintenancePM from "@/pages/maintenance/MaintenancePM";
import MaintenanceAMCs from "@/pages/maintenance/MaintenanceAMCs";
import FinanceShell from "@/pages/finance-os/FinanceShell";
import FinanceDashboard from "@/pages/finance-os/FinanceDashboard";
import FinancePayments from "@/pages/finance-os/FinancePayments";
import FinanceRefunds from "@/pages/finance-os/FinanceRefunds";
import FinanceDiscounts from "@/pages/finance-os/FinanceDiscounts";
import FinanceComps from "@/pages/finance-os/FinanceComps";
import FinanceCash from "@/pages/finance-os/FinanceCash";
import FinanceAudit from "@/pages/finance-os/FinanceAudit";
import FinanceEvents from "@/pages/finance-os/FinanceEvents";
import FnbShell from "@/pages/fnb/FnbShell";
import FnbDashboard from "@/pages/fnb/FnbDashboard";
import FnbPOSPage from "@/pages/fnb/FnbPOSPage";
import FnbTables from "@/pages/fnb/FnbTables";
import FnbKDS from "@/pages/fnb/FnbKDS";
import FnbBar from "@/pages/fnb/FnbBar";
import FnbPoolPage from "@/pages/fnb/FnbPoolPage";
import FnbRoomServicePage from "@/pages/fnb/FnbRoomServicePage";
import FnbBreakfast from "@/pages/fnb/FnbBreakfast";
import FnbMenu from "@/pages/fnb/FnbMenu";
import FnbReports from "@/pages/fnb/FnbReports";
import KitchenShell from "@/pages/kitchen/KitchenShell";
import KitchenDashboard from "@/pages/kitchen/KitchenDashboard";
import KitchenPantry from "@/pages/kitchen/KitchenPantry";
import PantryRequests from "@/pages/kitchen/PantryRequests";
import WasteManagement from "@/pages/kitchen/WasteManagement";
import KitchenExpediter from "@/pages/kitchen/KitchenExpediter";
import WAEShell from "@/pages/workflow/WAEShell";
import WAEDashboard from "@/pages/workflow/WAEDashboard";
import WAEInbox from "@/pages/workflow/WAEInbox";
import WAEMyRequests from "@/pages/workflow/WAEMyRequests";
import WAETemplates from "@/pages/workflow/WAETemplates";
import WAEDesigner from "@/pages/workflow/WAEDesigner";
import WAEDelegations from "@/pages/workflow/WAEDelegations";
import WAEEscalations from "@/pages/workflow/WAEEscalations";
import WAEHistory from "@/pages/workflow/WAEHistory";
import WAEAudit from "@/pages/workflow/WAEAudit";
import WAESettings from "@/pages/workflow/WAESettings";
import WAEChains from "@/pages/workflow/WAEChains";
import OpsShell from "@/pages/opshub/OpsShell";
import OpsDashboard from "@/pages/opshub/OpsDashboard";
import OpsLiveFeed from "@/pages/opshub/OpsLiveFeed";
import OpsNotifications from "@/pages/opshub/OpsNotifications";
import OpsTasks from "@/pages/opshub/OpsTasks";
import AIDecisions from "@/pages/opshub/AIDecisions";
import OpsBroadcasts from "@/pages/opshub/OpsBroadcasts";
import OpsAnnouncements from "@/pages/opshub/OpsAnnouncements";
import OpsMentions from "@/pages/opshub/OpsMentions";
import OpsTimeline from "@/pages/opshub/OpsTimeline";
import OpsSubscriptions from "@/pages/opshub/OpsSubscriptions";
import OpsSettings from "@/pages/opshub/OpsSettings";
import GuestShell from "@/pages/guests/GuestShell";
import GuestDashboard from "@/pages/guests/GuestDashboard";
import GuestDirectory from "@/pages/guests/GuestDirectory";
import GuestCompanies from "@/pages/guests/GuestCompanies";
import GuestAnalytics from "@/pages/guests/GuestAnalytics";
import GuestSettings from "@/pages/guests/GuestSettings";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="light" toastOptions={{ className: "font-sans text-[13px]" }} />
        <RegCardOptionsHost />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/force-password-change" element={
            <ProtectedRoute><ForcePasswordChange /></ProtectedRoute>
          } />
          {/* Platform Shell — Super Admin only (ADR-WF-1 v2.1 §26) */}
          <Route
            path="/platform"
            element={
              <ProtectedRoute requireSuperAdmin>
                <PlatformShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<PlatformDashboard />} />
            <Route path="hotels" element={<PlatformHotels />} />
            <Route path="hotels/new" element={<CreateHotelStep1 />} />
            <Route path="hotels/:hotelId/administrator" element={<CreateHotelStep2 />} />
            <Route path="hotels/:hotelId/services-setup" element={<CreateHotelStep3 />} />
            <Route path="hotels/:hotelId" element={<PlatformHotelDetail />} />
            <Route path="hotels/:hotelId/services" element={<PlatformHotelServices />} />
            <Route path="admins" element={<PlatformSuperAdmins />} />
            <Route path="events" element={<EventMonitor />} />
            <Route path="system-health" element={<PlatformSystemHealth />} />
            <Route path="account" element={<PlatformAccount />} />
          </Route>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="reports" element={<Reports />} />
            <Route path="roster" element={<RosterPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="dashboard/executive" element={<ExecutiveDashboard />} />
            <Route path="security" element={<SecurityDashboard />} />
            <Route path="team" element={<TeamMembers />} />
            <Route path="admin/readiness-catalogs" element={<ProtectedRoute requireSuperAdmin><ReadinessCatalogs /></ProtectedRoute>} />
            <Route path="housekeeping" element={<Housekeeping />} />
            <Route path="housekeeping/consumption" element={<RoomConsumption />} />
            <Route path="housekeeping/me" element={<MyTasks />} />
            <Route path="engineering" element={<Engineering />} />
            <Route path="engineering/me" element={<MyWorkOrders />} />
            <Route path="fo" element={<FrontDeskCommandCentre />} />
            <Route path="fo/shift" element={<ShiftPage />} />
            <Route path="setup" element={<SetupCenter />} />
            <Route path="hotel-settings/services" element={<HotelServicesEditor />} />
            <Route path="design-system/operational" element={<OperationalDemo />} />
            <Route path="design-system/operational/hk-os" element={<HkOsGallery />} />
            <Route path="hk/training" element={<HkTrainingApp />} />
            <Route path="hk/training/supervisor" element={<HkTrainingSupervisor />} />
            <Route path="hotels" element={<Hotels />} />
            <Route path="audit" element={<Audit />} />
            <Route path="events" element={<Events />} />
            <Route path="rules" element={<Rules />} />
            <Route path="scheduler" element={<Scheduler />} />
            <Route path="documents" element={<Documents />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="profile" element={<Profile />} />
            <Route path="master/:moduleKey" element={<MasterPage />} />
            <Route path="pms/guests" element={<Guests />} />
            <Route path="pms/guests/:id" element={<GuestDetail />} />
            <Route path="pms/stays" element={<Stays />} />
            <Route path="pms/stays/:id" element={<StayDetail />} />
            <Route path="pms/stays/:id/bill" element={<StayBillPage />} />
            <Route path="pms/reservations" element={<Reservations />} />
            <Route path="pms/reservations/:id" element={<ReservationDetail />} />
            <Route path="pms/arrivals" element={<ArrivalBoard />} />
            <Route path="quotations" element={<Quotations />} />
            <Route path="quotations/:id" element={<QuotationEditor />} />
            <Route path="pms/departures" element={<DepartureBoard />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="finance/folios" element={<Folios />} />
            <Route path="finance/folios/:id" element={<FolioWorkspace />} />
            <Route path="finance/payments" element={<Payments />} />
            <Route path="checkout" element={<Navigate to="/checkout/queue" replace />} />
            <Route path="checkout/queue" element={<CheckoutQueue />} />
            <Route path="checkout/:id" element={<CheckoutWorkspace />} />
            {/* AI Operations Center · Mission Control */}
            <Route path="operations" element={<OperationsPage />} />
            {/* HK-2 Phase 6 · Housekeeping Mission Control */}
            <Route path="housekeeping/mission-control" element={<HKMissionControl />} />
            {/* HK-2 Phase 7 · Housekeeping Copilot (mobile-first attendant app) */}
            <Route path="housekeeping/copilot" element={<HKCopilot />} />
            {/* HKINV · Housekeeping Inventory OS (Housekeeping ⇄ Storage) */}
            <Route path="housekeeping/inventory" element={<HKInventoryShell />}>
              <Route index element={<HKInventory />} />
              <Route path="requests" element={<HKStorageRequests />} />
              <Route path="floors" element={<HKFloorPars />} />
              <Route path="usage" element={<HKUsageLog />} />
            </Route>
            {/* Workforce OS · ADR-WF-1 v2.1 */}
            <Route path="wf" element={<WorkforceShell />}>
              <Route index element={<WorkforceDashboard />} />
              <Route path="employees" element={<WFEmployees />} />
              <Route path="system-access" element={<WFSystemAccess />} />
              <Route path="matrix" element={<WFPermissionMatrix />} />
              <Route path="organization" element={<WFOrganization />} />
              <Route path="policies" element={<WFPolicies />} />
              <Route path="devices" element={<WFDevices />} />
              <Route path="groups" element={<WFGroups />} />
              <Route path="approvals" element={<WFApprovals />} />
              <Route path="audit" element={<WFAudit />} />
            </Route>
            {/* SUP-1 · Supply OS · Inventory & Procurement */}
            <Route path="supply" element={<SupplyShell />}>
              <Route index element={<SupplyDashboard />} />
              <Route path="items" element={<SupplyItems />} />
              <Route path="vendors" element={<SupplyVendors />} />
              <Route path="stores" element={<SupplyStores />} />
              <Route path="stock" element={<SupplyStock />} />
              <Route path="prs" element={<SupplyPRs />} />
              <Route path="pos" element={<SupplyPOs />} />
              <Route path="grns" element={<SupplyGRNs />} />
              <Route path="settings" element={<SupplySettings />} />
            </Route>
            {/* SUP-2 · Inventory Digital Twin — dedicated full-screen dark mission control */}
            <Route path="supply/digital-twin" element={<DigitalTwin />} />
            {/* MAINT-1 · Maintenance OS */}
            <Route path="maintenance" element={<MaintenanceShell />}>
              <Route index element={<MaintenanceDashboard />} />
              <Route path="assets" element={<MaintenanceAssets />} />
              <Route path="work-orders" element={<MaintenanceWorkOrders />} />
              <Route path="preventive" element={<MaintenancePM />} />
              <Route path="amcs" element={<MaintenanceAMCs />} />
            </Route>
            {/* FIN-1 · Revenue & Finance OS */}
            <Route path="finance-os" element={<FinanceShell />}>
              <Route index element={<FinanceDashboard />} />
              <Route path="payments" element={<FinancePayments />} />
              <Route path="refunds" element={<FinanceRefunds />} />
              <Route path="discounts" element={<FinanceDiscounts />} />
              <Route path="comps" element={<FinanceComps />} />
              <Route path="cash" element={<FinanceCash />} />
              <Route path="audit" element={<FinanceAudit />} />
              <Route path="events" element={<FinanceEvents />} />
            </Route>
            {/* FNB-1 · F&B OS */}
            <Route path="fnb" element={<FnbShell />}>
              <Route index element={<FnbDashboard />} />
              <Route path="pos" element={<FnbPOSPage />} />
              <Route path="tables" element={<FnbTables />} />
              <Route path="kds" element={<FnbKDS />} />
              <Route path="bar" element={<FnbBar />} />
              <Route path="pool" element={<FnbPoolPage />} />
              <Route path="room-service" element={<FnbRoomServicePage />} />
              <Route path="breakfast" element={<FnbBreakfast />} />
              <Route path="menu" element={<FnbMenu />} />
              <Route path="reports" element={<FnbReports />} />
            </Route>
            {/* KOPS-1 · Kitchen Operations OS · Phase A0 */}
            <Route path="kitchen" element={<KitchenShell />}>
              <Route index element={<KitchenDashboard />} />
              <Route path="pantry" element={<KitchenPantry />} />
              <Route path="requests" element={<PantryRequests />} />
              <Route path="waste" element={<WasteManagement />} />
              <Route path="expo" element={<KitchenExpediter />} />
            </Route>
            {/* WAE-1 · Workflow & Approval Engine */}
            <Route path="workflow" element={<WAEShell />}>
              <Route index element={<WAEDashboard />} />
              <Route path="inbox" element={<WAEInbox />} />
              <Route path="my-requests" element={<WAEMyRequests />} />
              <Route path="chains" element={<WAEChains />} />
              <Route path="designer" element={<WAEDesigner />} />
              <Route path="templates" element={<WAETemplates />} />
              <Route path="delegations" element={<WAEDelegations />} />
              <Route path="escalations" element={<WAEEscalations />} />
              <Route path="history" element={<WAEHistory />} />
              <Route path="audit" element={<WAEAudit />} />
              <Route path="settings" element={<WAESettings />} />
            </Route>
            {/* OPS-1 · Operations Hub */}
            <Route path="operations-hub" element={<OpsShell />}>
              <Route index element={<OpsDashboard />} />
              <Route path="feed" element={<OpsLiveFeed />} />
              <Route path="notifications" element={<OpsNotifications />} />
              <Route path="tasks" element={<OpsTasks />} />
              <Route path="ai-decisions" element={<AIDecisions />} />
              <Route path="broadcasts" element={<OpsBroadcasts />} />
              <Route path="announcements" element={<OpsAnnouncements />} />
              <Route path="mentions" element={<OpsMentions />} />
              <Route path="timeline" element={<OpsTimeline />} />
              <Route path="subscriptions" element={<OpsSubscriptions />} />
              <Route path="settings" element={<OpsSettings />} />
            </Route>
            {/* GUEST-1 · Guest Experience OS */}
            <Route path="guests" element={<GuestShell />}>
              <Route index element={<GuestDashboard />} />
              <Route path="directory" element={<GuestDirectory />} />
              <Route path="companies" element={<GuestCompanies />} />
              <Route path="analytics" element={<GuestAnalytics />} />
              <Route path="settings" element={<GuestSettings />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
